import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./TidePostLanding.module.css";
import {
  HeroSection,
  FeaturesSection,
  HowItWorksSection,
  FAQSection,
  CTASection,
} from "../components/sections/TidePost";
import { navigationLinks, heroData } from "../data/tidePostData";

const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.logoIcon}>
    <path d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-.778.099-1.533.284-2.253" />
  </svg>
);

const AppStoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.appStoreIconSmall}>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

export const TidePostLanding: React.FC = () => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={styles.page}>
      {/* Skip Link */}
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>

      {/* Navigation */}
      <nav className={`${styles.nav} ${isScrolled ? styles.navScrolled : ""}`}>
        <div className={styles.navContainer}>
          <Link to="/tide-post" className={styles.logo}>
            <LogoIcon />
            <span>Tide Post</span>
          </Link>

          {/* Desktop Navigation */}
          <ul className={styles.navLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <button
                  onClick={() => scrollToSection(link.id)}
                  className={styles.navLink}
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>

          <a
            href={heroData.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.navCta}
          >
            <AppStoreIcon />
            <span>Download</span>
          </a>

          {/* Mobile Menu Button */}
          <button
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            <span className={`${styles.hamburger} ${isMobileMenuOpen ? styles.hamburgerOpen : ""}`} />
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuOpen : ""}`}>
          <ul className={styles.mobileNavLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <button
                  onClick={() => scrollToSection(link.id)}
                  className={styles.mobileNavLink}
                >
                  {link.label}
                </button>
              </li>
            ))}
            <li>
              <a
                href={heroData.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.mobileCta}
              >
                Download on App Store
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Main Content */}
      <main id="main-content" role="main" tabIndex={-1}>
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
        <FAQSection />
        <CTASection />
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <div className={styles.footerBrand}>
            <Link to="/tide-post" className={styles.footerLogo}>
              <LogoIcon />
              <span>Tide Post</span>
            </Link>
            <p className={styles.footerTagline}>Know your tides</p>
          </div>

          <div className={styles.footerLinks}>
            <div className={styles.footerColumn}>
              <h3 className={styles.footerHeading}>Product</h3>
              <ul>
                <li>
                  <button onClick={() => scrollToSection("features")}>Features</button>
                </li>
                <li>
                  <button onClick={() => scrollToSection("faq")}>FAQ</button>
                </li>
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h3 className={styles.footerHeading}>Legal</h3>
              <ul>
                <li>
                  <Link to="/tide-post/privacy-policy">Privacy Policy</Link>
                </li>
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h3 className={styles.footerHeading}>Company</h3>
              <ul>
                <li>
                  <Link to="/">Dixon App Development</Link>
                </li>
                <li>
                  <a href="mailto:dixonappdevelopment@gmail.com">Contact</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <p>&copy; {new Date().getFullYear()} Dixon App Development. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
